import type { TokenCredential } from "@azure/identity";
import { awaitDataPlaneReady, type ProbeResult } from "./data-plane-ready.js";
import { makeAgentProbe } from "./foundry-ready-probe.js";
import { LocalCliError } from "./errors.js";

/**
 * Poll until a freshly-deployed Foundry agent is queryable by name, or fail
 * clearly after a bounded budget. Foundry agent registration propagates
 * asynchronously after `createPromptVersion` returns, so a lookup-by-name
 * (GET /agents/{name}) can 404 for seconds-to-low-minutes. Mirrors the
 * project-level `awaitDataPlaneReady` (consecutive-success + bounded), scoped
 * to a single agent. On timeout throws a NON-retryable AGENT_NOT_QUERYABLE so
 * the installer's retry_classified fails fast (no nested re-poll); other a2a
 * failures keep their own classification.
 */
export async function awaitAgentQueryable(args: {
  credential: TokenCredential;
  projectEndpoint: string;
  agentName: string;
  consecutive?: number;
  attempts?: number;
  intervalMs?: number;
  sleep?: (ms: number) => Promise<void>;
  onProgress?: (m: string) => void;
  probe?: () => Promise<ProbeResult>;
}): Promise<{ attempts: number }> {
  const attemptsMax = args.attempts ?? 40;
  const intervalMs = args.intervalMs ?? 3_000;
  const probe = args.probe ?? makeAgentProbe(args.credential, args.projectEndpoint, args.agentName);

  const { ready, attempts } = await awaitDataPlaneReady({
    probe,
    consecutive: args.consecutive ?? 2,
    attempts: attemptsMax,
    intervalMs,
    sleep: args.sleep,
    onProgress: args.onProgress,
    retryableStatuses: [404],
  });

  if (!ready) {
    const approxSec = Math.round((attemptsMax * intervalMs) / 1000);
    throw new LocalCliError({
      code: "AGENT_NOT_QUERYABLE",
      message: `Agent '${args.agentName}' did not become queryable within ${attempts.toString()} probes (~${approxSec.toString()}s) after deploy.`,
      hint: "Foundry agent registration propagates asynchronously after deploy; a retry usually clears it. Re-run shortly (e.g. 'm8t bootstrap launch' or 'm8t a2a enable <name>').",
      category: "data_plane_not_ready",
      retryable: false,
    });
  }
  return { attempts };
}
